import { useEffect, useState } from 'react'
import { healthConfig, shouldEnableHealthChecks } from '../config/healthConfig'
import { createLogger } from '../utils/logger'

const logger = createLogger('ServerConnectionBanner')

export default function ServerConnectionBanner() {
  const [isOnline, setIsOnline] = useState(true)
  const [lastError, setLastError] = useState('')
  const [isChecking, setIsChecking] = useState(false)

  useEffect(() => {
    // Check if health checks should be enabled
    if (!shouldEnableHealthChecks()) {
      logger.debug('🏥 Health checks disabled by configuration')
      return
    }

    let isMounted = true
    const controller = new AbortController()
    let retryTimeout = null
    let checkInterval = null

    async function ping() {
      if (!isMounted) return

      setIsChecking(true)
      try {
        // Use fetch with proper timeout and abort signal
        const res = await fetch(healthConfig.endpoint, {
          signal: controller.signal,
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        })

        if (!res.ok) {
          throw new Error(`Health check failed: ${res.status} ${res.statusText}`)
        }

        const data = await res.json()

        if (isMounted) {
          setIsOnline(true)
          setLastError('')
          setIsChecking(false)
        }
      } catch (err) {
        // Don't treat aborted requests as errors (component unmounting)
        if (err.name === 'AbortError') {
          return
        }

        logger.warn('🏥 Server health check failed:', err.message)

        if (isMounted) {
          setIsOnline(false)
          setLastError(err?.message || 'Unable to reach server')
          setIsChecking(false)

          // Schedule a retry
          if (retryTimeout) clearTimeout(retryTimeout)
          retryTimeout = setTimeout(() => {
            if (isMounted && !isOnline) {
              ping()
            }
          }, healthConfig.retryInterval)
        }
      }
    }

    // Initial ping
    ping()

    // Set up periodic health checks when online
    const startPeriodicChecks = () => {
      if (checkInterval) clearInterval(checkInterval)
      checkInterval = setInterval(() => {
        if (isMounted && isOnline) {
          ping()
        }
      }, healthConfig.checkInterval)
    }

    // Start periodic checks after initial success
    if (isOnline) {
      startPeriodicChecks()
    }

    return () => {
      isMounted = false
      controller.abort()
      if (retryTimeout) clearTimeout(retryTimeout)
      if (checkInterval) clearInterval(checkInterval)
    }
  }, [])

  // Don't render if health checks are disabled
  if (!shouldEnableHealthChecks()) {
    return null
  }

  // Don't render if online
  if (isOnline) return null

  return (
    <div className="w-full bg-red-600 text-white text-sm px-4 py-2">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <span>
          {isChecking ? 'Checking server connection...' : 'Cannot reach server. Retrying...'}
          {lastError && !isChecking && ` (${lastError})`}
        </span>
        <button
          onClick={() => window.location.reload()}
          className="ml-4 px-2 py-1 bg-red-700 hover:bg-red-800 rounded text-xs"
        >
          Retry Now
        </button>
      </div>
    </div>
  )
}
